<!--
  ValueField — value-kind renderer (string/number/timestamp/currency/…); the
  chrome-free inner GridCell hosts. `modelValue` is the value to render, `mode`
  its lifecycle (inline | active | editing). Inline shows the formatted value;
  active/editing show the field's form input, read-only until editing. `seeded`
  places the caret at the end for type-to-edit rather than selecting all.
-->
<template>
  <div v-if="mode === 'inline' || !inputComponent" class="value-field" :class="sizeClass">
    <span v-if="isEmpty" class="cell-placeholder">{{ placeholderHint }}</span>
    <span v-else class="cell-value">{{ displayValue }}</span>
  </div>
  <component
    :is="inputComponent"
    v-else
    ref="input"
    class="value-field value-field-editor"
    :model-value="modelValue"
    :field="field"
    :size="size"
    :placeholder="placeholderHint"
    :multiline="isStringField"
    :readonly="mode !== 'editing'"
    @update:model-value="emit('update:modelValue', $event)"
    @keydown.enter="onEnter"
  />
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue"
import { type AugerField, formatValue } from "@auger/core/fields"
import { getComponent } from "../registry"
import { type FieldMode, type FieldSize, textSizeClass } from "../useField"

const props = withDefaults(
  defineProps<{
    modelValue: unknown
    field: AugerField
    mode: FieldMode
    size?: FieldSize
    seeded?: boolean
  }>(),
  { size: "small", seeded: false }
)

const emit = defineEmits<{
  "update:modelValue": [value: unknown]
  commit: [payload?: { advance?: boolean }]
  cancel: []
}>()

const inputComponent = computed(() => getComponent("form", props.field))
const isStringField = computed(() => props.field.type === "string")

const sizeClass = computed(() => textSizeClass(props.size))
const editable = computed(() => !!props.field.isEditable)

const displayValue = computed(() => {
  const formatted = formatValue(props.field, props.modelValue)
  return formatted == null ? "" : String(formatted)
})
const isEmpty = computed(() => displayValue.value === "")

// Per-type hint mirroring the expected value shape; blank when not editable.
const PLACEHOLDER_HINTS: Record<string, string> = {
  timestamp: "mm/dd/yyyy, --:--",
  number: "0",
  integer: "0",
  currency: "$0.00",
  percent: "0%",
}
const placeholderHint = computed(() => {
  if (!editable.value) return ""
  const fieldPlaceholder = props.field.placeholder
  if (typeof fieldPlaceholder === "string" && fieldPlaceholder !== "") return fieldPlaceholder
  return PLACEHOLDER_HINTS[props.field.type as string] ?? "Empty"
})

const input = ref<{ $el?: HTMLElement } | null>(null)
function editorEl(): HTMLInputElement | HTMLTextAreaElement | null {
  const root = input.value?.$el ?? (input.value as unknown as HTMLElement | null)
  const el = root?.matches?.("input, textarea") ? root : root?.querySelector?.("input, textarea")
  return (el as HTMLInputElement | HTMLTextAreaElement | null) ?? null
}

// On edit-open, focus the input. Click/Enter opens on the stored value (select
// all, first keystroke replaces); type-to-edit opens on a seeded value (`seeded`),
// so place the caret at the end to append.
watch(
  () => props.mode === "editing",
  (on) => {
    if (!on) return
    const typeToEdit = props.seeded
    nextTick(() => {
      const el = editorEl()
      if (!el) return
      el.focus()
      if (typeToEdit) {
        const end = el.value.length
        el.setSelectionRange(end, end)
      } else {
        el.select()
      }
    })
  }
)

// Enter commits AND advances (the data-entry loop). A string textarea keeps
// Enter for newlines and commits on Cmd/Ctrl+Enter.
function onEnter(event: KeyboardEvent) {
  if (isStringField.value && !event.metaKey && !event.ctrlKey) return
  event.preventDefault()
  emit("commit", { advance: true })
}
</script>

<style>
.value-field {
  display: flex;
  align-items: center;
  min-width: 0;
  height: 100%;
  overflow: hidden;
  cursor: default;

  /* Empty-cell hint: hidden until hover, then a faint prompt. */
  .cell-placeholder {
    color: var(--auger-text-weak);
    opacity: 0;
    transition: opacity 0.1s ease;
  }

  &:hover .cell-placeholder {
    opacity: 0.6;
  }
}

.grid-editor {
  textarea.value-field-editor,
  &.is-editing textarea.value-field-editor {
    /* Nudge the editor to align with the in-row text. */
    margin-top: 2.5px;
    margin-left: 1px;
    /* The shared `.value-field` class hands this textarea `display: flex` +
       `height: 100%` (meant for the inline value container) — reset both so
       StringInput's autosize owns the height and the box grows to the value.
       Stretching to the row instead, as this used to, made the editor exactly
       as tall as the row it opened over: one line on a short row, however long
       the text. `max-height` caps it so an essay scrolls rather than filling
       the screen; `resize: none` because autosize is the mechanism. */
    display: block;
    height: auto;
    resize: none;
    max-height: 40vh;
    width: 100%;
  }

  input.value-field-editor {
    /* Nudge the editor to align with the in-row text. */
    margin-top: -1px;
    margin-left: 1px;
    width: 100%;
  }
}
</style>
